import Alpine from 'alpinejs';
import { getCSRFToken } from '../utils/getCSRFToken';

export default function registerCalendarSettingsAccountStore() {
  const existing = Alpine.store('calendarSettingsAccountStore');
  if (existing) return existing;

  const baseStore = Alpine.store('calendarSettingsStore');
  if (!baseStore) return null;

  const accountStore = {};
  Object.defineProperties(
    accountStore,
    Object.getOwnPropertyDescriptors(baseStore),
  );

  const accountExtensions = {
    slug: '',
    originalSlug: '',
    existingSlugs: [],
    isSlugTaken: false,
    originalBusinessName: '',
    businessNameEdited: false,
    selectedExportYear: 2025,
    availableYears: [],
    isExporting: false,
    exportMessage: '',
    exportError: false,
    exportJobId: null,
    exportProgressPercent: 0,
    exportPollInterval: null,
    lastCompletedExport: null,
    async init() {
      this.initializeSettingsContext();
      this.initializeSlug();
      this.initializeAvailableYears();
      this.fetchCustomFields();
      this.fetchListingFields();
      window.setTimeout(() => this.ensureModalBindings(), 0);
    },

    initializeSlug() {
      const container = document.getElementById('calendarSettingsContainer');
      this.slug = container?.dataset?.currentSlug || '';
      this.originalSlug = this.slug;

      const businessNameInput = document.querySelector('input[name="name"]');
      this.originalBusinessName = (businessNameInput?.value || '').trim();
      this.businessNameEdited = false;

      try {
        const existingSlugsScript = document.getElementById(
          'existing-slugs-data',
        );
        if (existingSlugsScript) {
          const existingSlugsData = existingSlugsScript.textContent;
          this.existingSlugs = JSON.parse(existingSlugsData);
        } else {
          this.existingSlugs = [];
        }
      } catch (error) {
        console.error('Error loading existing slugs:', error);
        this.existingSlugs = [];
      }
    },

    updateSlug(businessName) {
      const normalizedBusinessName = (businessName || '').trim();
      this.businessNameEdited =
        normalizedBusinessName !== this.originalBusinessName;

      const newSlug = normalizedBusinessName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      this.slug = newSlug;

      try {
        const slugsArray =
          typeof this.existingSlugs === 'string'
            ? JSON.parse(this.existingSlugs || '[]')
            : this.existingSlugs || [];

        this.isSlugTaken =
          !!newSlug &&
          newSlug !== this.originalSlug &&
          slugsArray.includes(newSlug);
      } catch (error) {
        console.error('Error parsing existing slugs:', error);
        this.isSlugTaken = false;
      }
    },

    initializeAvailableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear - 10; i <= currentYear + 1; i++) {
        years.push(i);
      }
      this.availableYears = years.reverse();
      this.selectedExportYear = currentYear;
      this.loadLatestExport();
    },

    async exportYearlyBookings() {
      if (!this.calendarId || !this.selectedExportYear) {
        this.exportError = true;
        this.exportMessage = 'Missing calendar or year selection';
        return;
      }

      this.isExporting = true;
      this.exportError = false;
      this.exportMessage = 'Starting export...';
      this.exportProgressPercent = 0;

      try {
        const response = await fetch('/calendar/export-yearly-bookings/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            year: this.selectedExportYear,
            calendar_id: this.calendarId,
          }),
          credentials: 'same-origin',
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Export failed');
        }

        const data = await response.json();
        this.exportJobId = data.job_id;
        this.exportMessage = data.progress_message || 'Export started...';
        this.exportProgressPercent = data.progress_percent || 0;
        this.startExportPolling();
      } catch (error) {
        console.error('Error starting export:', error);
        this.exportError = true;
        this.exportMessage =
          error.message || 'An error occurred while starting the export';
        this.isExporting = false;
      }
    },

    startExportPolling() {
      if (this.exportPollInterval) {
        clearInterval(this.exportPollInterval);
      }
      this.exportPollInterval = setInterval(async () => {
        await this.checkExportProgress();
      }, 5000);
    },

    async checkExportProgress() {
      if (!this.exportJobId) {
        this.stopExportPolling();
        return;
      }

      try {
        const response = await fetch(
          `/calendar/export-status/${this.exportJobId}/`,
          {
            method: 'GET',
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
            credentials: 'same-origin',
          },
        );

        if (!response.ok) {
          throw new Error('Failed to check export status');
        }

        const data = await response.json();
        this.exportMessage = data.progress_message;
        this.exportProgressPercent = data.progress_percent;

        if (data.status === 'completed') {
          this.stopExportPolling();
          this.isExporting = false;
          this.exportError = false;
          this.exportMessage = 'Export complete! Click to download.';
          this.lastCompletedExport = {
            jobId: this.exportJobId,
            fileName: data.file_name,
            year: this.selectedExportYear,
            completedAt: data.completed_at,
          };
        } else if (data.status === 'failed') {
          this.stopExportPolling();
          this.isExporting = false;
          this.exportError = true;
          this.exportMessage =
            data.error_message || 'Export failed. Please try again.';

          setTimeout(() => {
            this.exportMessage = '';
            this.exportError = false;
          }, 10000);
        }
      } catch (error) {
        console.error('Error checking export progress:', error);
      }
    },

    stopExportPolling() {
      if (this.exportPollInterval) {
        clearInterval(this.exportPollInterval);
        this.exportPollInterval = null;
      }
    },

    downloadExport(jobId) {
      const downloadJobId =
        jobId ||
        (this.lastCompletedExport ? this.lastCompletedExport.jobId : null);

      if (!downloadJobId) {
        console.error('No export job ID to download');
        return;
      }

      window.location.href = `/calendar/export-download/${downloadJobId}/`;
    },

    async loadLatestExport() {
      try {
        const response = await fetch('/calendar/export-latest/', {
          method: 'GET',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
          credentials: 'same-origin',
        });

        if (response.ok) {
          const data = await response.json();
          if (data.job && data.job.status === 'completed') {
            this.lastCompletedExport = {
              jobId: data.job.id,
              fileName: data.job.file_name,
              year: data.job.year,
              completedAt: data.job.completed_at,
            };
          }
        }
      } catch (error) {
        console.error('Error loading latest export:', error);
      }
    },
  };

  Object.defineProperties(
    accountStore,
    Object.getOwnPropertyDescriptors(accountExtensions),
  );

  return Alpine.store('calendarSettingsAccountStore', accountStore);
}
